import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, tokens } from "@fluentui/react-components";
import { PersonAdd24Regular } from "@fluentui/react-icons";
import type { CreateClientInput } from "@gymplan/shared";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { ClientForm } from "../components/ClientForm.tsx";
import { WEEKDAY_SHORT } from "../lib/format.ts";
import { ImportClientsDialog } from "../components/ImportClients.tsx";
import { PeopleAdd24Regular } from "@fluentui/react-icons";

export function ClientsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user } = useAuth();
  const gyms = user?.trainer?.gyms ?? [];
  const q = useQuery({ queryKey: ["clients"], queryFn: () => api.get<Client[]>("/api/clients") });
  const avail = useQuery({ queryKey: ["availability"], queryFn: () => api.get<{ slots: AvailabilitySlot[] }>("/api/trainers/me/availability").then((r) => r.slots) });
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const create = useMutation({
    mutationFn: (v: CreateClientInput) => api.post<Client>("/api/clients", v),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Text as="h1" size={800} weight="semibold" style={{ flex: 1 }}>
          Clients
        </Text>
        <Button appearance="primary" icon={<PeopleAdd24Regular />} onClick={() => setImportOpen(true)}>
          Import clients
        </Button>
        <Button icon={<PersonAdd24Regular />} onClick={() => setOpen(true)}>
          Add one
        </Button>
      </div>
      <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: 16 }}>
        Import names and numbers and RackUp invites each client on your behalf; or share your sign-up link from the home page and they add themselves.
      </Text>

      <Table aria-label="Clients">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>WhatsApp</TableHeaderCell>
            <TableHeaderCell>Sessions / week</TableHeaderCell>
            <TableHeaderCell>Preferred days</TableHeaderCell>
            <TableHeaderCell>Check-ins</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(q.data ?? []).map((c) => (
            <TableRow key={c.id} onClick={() => nav(`/clients/${c.id}`)} style={{ cursor: "pointer" }}>
              <TableCell>
                {c.name}
                {c.whatsappOptIn === false && (
                  <Badge appearance="tint" color="warning" size="small" style={{ marginLeft: 8 }}>
                    opted out of WhatsApp
                  </Badge>
                )}
                {!c.joinedAt && (
                  <Badge appearance="tint" color="informative" size="small" style={{ marginLeft: 8 }}>
                    invited — not joined yet
                  </Badge>
                )}
              </TableCell>
              <TableCell>{c.phone}</TableCell>
              <TableCell>{c.joinedAt ? c.sessionsPerWeek : "—"}</TableCell>
              <TableCell>{[...new Set(c.preferences.map((p) => WEEKDAY_SHORT[p.weekday as keyof typeof WEEKDAY_SHORT]))].join(", ") || "—"}</TableCell>
              <TableCell>{!c.joinedAt ? "—" : c.askEachWeek === false ? "Usual times, not asked" : "Every week"}</TableCell>
            </TableRow>
          ))}
          {q.data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>No clients yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ImportClientsDialog open={importOpen} onClose={() => setImportOpen(false)} trainerFirst={user?.name.split(" ")[0] ?? ""} />
      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface style={{ maxWidth: 1000 }}>
          <DialogBody>
            <DialogTitle>Add client</DialogTitle>
            <DialogContent>
              <ClientForm gyms={gyms} availability={avail.data} sessionLengthMinutes={user?.trainer?.sessionLengthMinutes} requireAttestation onSubmit={(v) => create.mutateAsync(v).then(() => undefined)} submitLabel="Add client" busy={create.isPending} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
